import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { Snackbar, Alert } from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import moment from 'moment';
import { GetAll_CancelReasons_Data } from '../../Redux/Actions/CancelReasons_Actions';
import { ReasonActions } from './ReasonActions';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconSearch   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IconFilter   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IconCopy     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IconPlus     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconReason   = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const IconTrend    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
const IconCalendar = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;

export const CancelReasonTable = () => {
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const dispatch     = useDispatch();
  const sidebarWidth = useSidebarWidth();

  const [loading,      setLoading]      = useState(true);
  const [userlogin,    setUserlogin]    = useState(null);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [showFilter,   setShowFilter]   = useState(false);
  const [copyOpen,     setCopyOpen]     = useState(false);
  const [page,         setPage]         = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));
  }, []);

  const fetchReasons = async () => {
    setLoading(true);
    await dispatch(GetAll_CancelReasons_Data());
    setLoading(false);
  };
  useEffect(() => { fetchReasons(); }, []);

  const res        = useSelector((s) => s.AllCancelReasons.Cancelreason);
  const allReasons = Array.isArray(res?.data) ? res.data : [];

  /* filter */
  const filtered = allReasons.filter((r) => {
    const q = searchTerm.toLowerCase();
    return !q || r.cancelreason?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const pageItems  = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  /* stats */
  const thisMonth    = allReasons.filter(r => moment(r.createdAt).isAfter(moment().subtract(30, 'days'))).length;
  const newestReason = allReasons.length
    ? allReasons.reduce((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? a : b)
    : null;

  /* routes */
  const homeRoute =
    userlogin?.data?.role === 'Admin'    ? '/AllLeadsForAdmin'
  : userlogin?.data?.role === 'Marketer' ? '/ALLLeadsForMarketer'
  : '/';

  /* page numbers */
  const buildPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    if (page <= 4)                  pages.push(1, 2, 3, 4, 5, '...', totalPages);
    else if (page >= totalPages -3) pages.push(1, '...', totalPages-4, totalPages-3, totalPages-2, totalPages-1, totalPages);
    else                            pages.push(1, '...', page-1, page, page+1, '...', totalPages);
    return pages;
  };

  const pBtn = (disabled, active = false) => ({
    minWidth: '34px', height: '34px', padding: '0 10px',
    border: `1px solid ${isDark ? '#3a3a5a' : '#E8EAF0'}`,
    backgroundColor: active ? '#3b82f6' : (isDark ? '#2d2e42' : '#ffffff'),
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    color: active ? '#ffffff' : (isDark ? '#cbd5e1' : '#4b5563'),
    fontSize: '13px', fontWeight: active ? 600 : 500,
    transition: 'all 0.2s ease', fontFamily: "'DM Sans', sans-serif",
  });
  const pHover = (e, disabled) => {
    if (!disabled) {
      e.currentTarget.style.backgroundColor = isDark ? '#3b82f6' : '#f3f4f6';
      e.currentTarget.style.borderColor = '#3b82f6';
      e.currentTarget.style.color = isDark ? '#ffffff' : '#3b82f6';
    }
  };
  const pLeave = (e, disabled) => {
    if (!disabled) {
      e.currentTarget.style.backgroundColor = isDark ? '#2d2e42' : '#ffffff';
      e.currentTarget.style.borderColor = isDark ? '#3a3a5a' : '#E8EAF0';
      e.currentTarget.style.color = isDark ? '#cbd5e1' : '#4b5563';
    }
  };

  /* ── render ── */
  return (
    <>
      <style>{`.ap-pagination-fixed { position: fixed !important; }`}</style>

      {/* ✅ نفس pattern بتاع AllCitiesShow — marginLeft مش position fixed */}
      <div
        className={`ap-shell ${isDark ? 'dark' : ''}`}
        style={{
          position: "fixed",
          top: "50px",
          left: sidebarWidth,
          right: 0,
          bottom: 0,
          transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >

        {/* ─── Breadcrumb ─── */}
        <section style={{ padding: '20px 28px 0', marginBottom: '-8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <Link to={homeRoute} style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>
              Home
            </Link>
            <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
            <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
              Cancel Reasons
            </span>
          </div>
        </section>

        {/* ═══ STATS ═══ */}
        <section className="ap-stats" style={{ padding: '24px 28px 0' }}>

          <div className="ap-stat-card light">
            <div>
              <div className="ap-stat-label">TOTAL REASONS</div>
              <div className="ap-stat-value">{allReasons.length}</div>
              <div className="ap-stat-badge green">↑ Active in system</div>
            </div>
            <div className="ap-stat-icon light-icon"><IconReason /></div>
          </div>

          <div className="ap-stat-card light">
            <div>
              <div className="ap-stat-label">ADDED THIS MONTH</div>
              <div className="ap-stat-value large">{thisMonth}</div>
              <div className="ap-stat-badge green">Last 30 days</div>
            </div>
            <div className="ap-stat-icon light-icon"><IconCalendar /></div>
          </div>

          <div className="ap-stat-card dark-card">
            <div>
              <div className="ap-stat-label white">LATEST REASON</div>
              <div className="ap-stat-value white" style={{ fontSize: 18, marginTop: 4 }}>
                {newestReason?.cancelreason || '—'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                <span className="ap-badge-pill">MOST RECENT</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                  {newestReason ? moment(newestReason.createdAt).format('MMM DD, YYYY') : ''}
                </span>
              </div>
            </div>
            <div className="ap-stat-icon dark-icon"><IconTrend /></div>
          </div>

        </section>

        {/* ═══ TABLE SECTION ═══ */}
        <section className="ap-table-section" style={{ margin: '24px 28px', flex: 1, paddingBottom: '80px' }}>

          {/* header */}
          <div className="ap-table-header">
            <div>
              <div className="ap-table-title-label">SYSTEM</div>
              <div className="ap-table-title">CANCEL REASONS</div>
            </div>
            <div className="ap-table-actions">
              <div className="ap-search-inline">
                <IconSearch />
                <input
                  placeholder="Search reasons…"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                />
              </div>
              <button className="ap-filter-btn" onClick={() => setShowFilter(!showFilter)}>
                <IconFilter /> Filter
              </button>
              <Link to="/addreason" style={{ textDecoration: 'none' }}>
                <button className="ap-create-btn">
                  <IconPlus /> Add Reason
                </button>
              </Link>
            </div>
          </div>

          {/* filter row */}
          {showFilter && (
            <div className="ap-filter-row">
              <button className="ap-clear-btn" onClick={() => { setSearchTerm(''); setPage(1); }}>
                Clear
              </button>
            </div>
          )}

          {/* table */}
          <div className="ap-table-wrap">
            <table className="ap-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}><input type="checkbox" className="ap-checkbox" /></th>
                  <th>REASON TITLE</th>
                  <th>CREATED DATE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="ap-empty">
                      <div className="ap-spinner" />
                      <span>Loading reasons…</span>
                    </td>
                  </tr>
                ) : pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="ap-empty">
                      <IconSearch />
                      <p>No cancel reasons found</p>
                    </td>
                  </tr>
                ) : (
                  pageItems.map((item, i) => (
                    <tr key={item._id || i} className="ap-row">
                      <td><input type="checkbox" className="ap-checkbox" /></td>

                      {/* Reason Title */}
                      <td>
                        <div className="ap-project-cell">
                          <div className="ap-project-thumb">
                            {item.cancelreason?.slice(0, 2).toUpperCase() || 'CR'}
                          </div>
                          <span className="ap-project-name">{item.cancelreason || '—'}</span>
                        </div>
                      </td>

                      {/* Date */}
                      <td className="ap-cell-muted" style={{ fontSize: 13 }}>
                        {item.createdAt ? moment(item.createdAt).format('MMM DD, YYYY') : '—'}
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="ap-actions">
                          <CopyToClipboard
                            text={item._id}
                            onCopy={() => { setCopyOpen(true); setTimeout(() => setCopyOpen(false), 2000); }}
                          >
                            <button className="ap-action-btn" title="Copy ID">
                              <IconCopy />
                            </button>
                          </CopyToClipboard>
                          <ReasonActions cancelreason={item} allReasons={fetchReasons} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </section>

        {/* footer */}
        <footer className="ap-footer" style={{ padding: '16px 28px', marginTop: 'auto' }}>
          <span>© 2024 Realatix CRM. Architectural & Digital Concierge Excellence.</span>
          <div className="ap-footer-links">
            <span>Privacy Protocol</span>
            <span>Terms of Engagement</span>
          </div>
        </footer>
      </div>

      {/* ✅ Fixed Pagination — خارج الـ main div زي AllCitiesShow */}
      {filtered.length > 0 && (
        <div
          className="ap-pagination ap-pagination-fixed"
          style={{
            bottom: '0',
            left: sidebarWidth,
            right: '0',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            padding: '12px 28px',
            backgroundColor: isDark ? '#1e1f2e' : '#ffffff',
            borderTop: `1px solid ${isDark ? '#2a2a45' : '#E8EAF0'}`,
            boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
            transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          {/* Items per page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#7B829A' }}>Items per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
              style={{
                padding: '6px 10px', borderRadius: '8px',
                border: `1px solid ${isDark ? '#3a3a5a' : '#E8EAF0'}`,
                backgroundColor: isDark ? '#2d2e42' : '#ffffff',
                color: isDark ? '#e5e7eb' : '#1f2937',
                fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {[5, 10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#7B829A' }}>
              Showing{' '}
              <strong style={{ color: isDark ? '#e5e7eb' : '#1f2937' }}>
                {(page - 1) * itemsPerPage + 1}–{Math.min(page * itemsPerPage, filtered.length)}
              </strong>
              {' '}of{' '}
              <strong style={{ color: isDark ? '#e5e7eb' : '#1f2937' }}>{filtered.length}</strong> reasons
            </span>
          </div>

          {/* Page buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <button onClick={() => setPage(1)} disabled={page === 1} style={pBtn(page === 1)}
              onMouseEnter={(e) => pHover(e, page === 1)} onMouseLeave={(e) => pLeave(e, page === 1)}>⟨|</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pBtn(page === 1)}
              onMouseEnter={(e) => pHover(e, page === 1)} onMouseLeave={(e) => pLeave(e, page === 1)}>‹</button>

            {buildPageNumbers().map((n, idx) =>
              n === '...'
                ? <span key={`e-${idx}`} style={{ color: isDark ? '#9ca3af' : '#7B829A', padding: '0 4px' }}>…</span>
                : (
                  <button key={n} onClick={() => setPage(n)} style={pBtn(false, page === n)}
                    onMouseEnter={(e) => { if (page !== n) pHover(e, false); }}
                    onMouseLeave={(e) => { if (page !== n) pLeave(e, false); }}>
                    {n}
                  </button>
                )
            )}

            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pBtn(page === totalPages)}
              onMouseEnter={(e) => pHover(e, page === totalPages)} onMouseLeave={(e) => pLeave(e, page === totalPages)}>›</button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pBtn(page === totalPages)}
              onMouseEnter={(e) => pHover(e, page === totalPages)} onMouseLeave={(e) => pLeave(e, page === totalPages)}>|⟩</button>
          </div>
        </div>
      )}

      {/* Snackbar */}
      <Snackbar open={copyOpen} autoHideDuration={2000} onClose={() => setCopyOpen(false)}>
        <Alert severity="success" sx={{ width: '100%' }}>Copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};
